import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartPulse, Users, ShieldCheck, Award, Clock, MapPin } from "lucide-react";
import { CLASSES, IMAGES, CONTACT, priceLabel } from "@/data/classes";
import Seo, { orgJsonLd } from "@/components/Seo";

export default function Home() {
  return (
    <div>
      <Seo
        path="/"
        description="AHA-aligned BLS, ACLS, PALS, Heartsaver CPR/First Aid, and workplace safety training for healthcare professionals, medical practices, and businesses across the DFW area. Sign up for a class today."
        jsonLd={orgJsonLd}
      />
      {/* HERO */}
      <section className="relative bg-white border-b border-gray-200 grain-overlay">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-[#d90429] mb-6">
              DFW Area · AHA-Aligned Training
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black leading-none text-[#0a0a0a]">
              Life-saving
              <br />
              skills.
              <br />
              <span className="text-[#d90429]">Delivered hands-on.</span>
            </h1>
            <p className="mt-8 text-lg text-gray-700 max-w-xl leading-relaxed">
              BLS, ACLS, PALS, Heartsaver, and workplace safety training for
              healthcare professionals, medical practices, and businesses across
              the Dallas–Fort Worth area.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/schedule"
                data-testid="hero-signup-cta"
                className="inline-flex items-center gap-2 rounded-md bg-[#d90429] px-8 py-4 text-base font-bold text-white hover:bg-black transition-colors focus-ring"
              >
                Sign Up for a Class <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/classes"
                data-testid="hero-view-classes"
                className="inline-flex items-center gap-2 rounded-md border-2 border-[#0a0a0a] px-8 py-4 text-base font-bold text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                Explore All Classes
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-8 text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <Award className="w-5 h-5 text-[#d90429]" /> AHA-Aligned Curriculum
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Users className="w-5 h-5 text-[#d90429]" /> Small Class Sizes
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <MapPin className="w-5 h-5 text-[#d90429]" /> On-Site Available
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-lg border border-gray-200">
              <img
                src={IMAGES.hero_cpr}
                alt="CPR training with AED on manikin"
                className="w-full h-[420px] lg:h-[520px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <div className="font-mono-label text-white/70 mb-2">FEATURED</div>
                <div className="text-white font-display text-2xl font-bold">
                  Real skills. Real practice. Real cards.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE STRIP */}
      <section className="bg-[#f9fafb] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-3 gap-8">
          {[
            { icon: HeartPulse, title: "Healthcare Professionals", body: "BLS, ACLS, PALS provider & renewal courses that keep your card current." },
            { icon: Users, title: "Medical Practices", body: "Bring the whole team up to date — flexible scheduling for clinics and practices." },
            { icon: ShieldCheck, title: "Businesses & Groups", body: "OSHA-aligned safety and Heartsaver programs delivered on-site at your workplace." }
          ].map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="p-8 bg-white border border-gray-200 hover-lift rounded-lg"
              data-testid={`audience-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <Icon className="w-8 h-8 text-[#d90429]" />
              <h3 className="mt-6 font-display text-2xl font-bold text-[#0a0a0a]">{title}</h3>
              <p className="mt-3 text-gray-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CLASSES BENTO */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="font-mono-label text-gray-500 mb-3">Our Classes</div>
              <h2 className="font-display text-4xl md:text-5xl font-black text-[#0a0a0a]">
                Every course we teach.
              </h2>
            </div>
            <Link
              to="/schedule"
              data-testid="classes-section-signup-cta"
              className="inline-flex items-center gap-2 rounded-md bg-[#d90429] px-6 py-3 text-sm font-bold text-white hover:bg-black transition-colors"
            >
              Sign Up for a Class <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CLASSES.map((c, idx) => (
              <Link
                key={c.slug}
                to={`/classes/${c.slug}`}
                data-testid={`home-class-card-${c.slug}`}
                className={`group p-8 border border-gray-200 hover-lift bg-white flex flex-col rounded-lg ${
                  idx === 0 ? "lg:col-span-2 lg:row-span-2 lg:min-h-[420px]" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono-label text-[#d90429]">{c.code}</div>
                  <div className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center group-hover:bg-[#d90429] group-hover:border-[#d90429] transition-colors">
                    <ArrowRight className="w-4 h-4 text-gray-700 group-hover:text-white" />
                  </div>
                </div>
                <h3
                  className={`mt-6 font-display font-black text-[#0a0a0a] leading-tight ${
                    idx === 0 ? "text-3xl md:text-4xl" : "text-2xl"
                  }`}
                >
                  {c.name}
                </h3>
                <p className="mt-4 text-gray-600 leading-relaxed flex-1">{c.tagline}</p>
                <div className="mt-6 flex items-center gap-4 text-xs text-gray-500 pt-4 border-t border-gray-200">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {c.duration}</span>
                  <span>{priceLabel(c)}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#0a0a0a] text-white grain-overlay">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="font-mono-label text-[#d90429] mb-4">Why Rhythm Check</div>
            <h2 className="font-display text-4xl md:text-5xl font-black leading-tight">
              We train people who save lives —{" "}
              <span className="text-[#d90429]">and we take that seriously.</span>
            </h2>
            <p className="mt-6 text-gray-300 text-lg leading-relaxed">
              Every class is hands-on, small-group, and taught by experienced
              instructors. You leave with skills you can actually use, and
              certifications your employer accepts.
            </p>
            <Link
              to="/about"
              data-testid="why-us-about-link"
              className="mt-8 inline-flex items-center gap-2 font-bold text-white hover:text-[#d90429]"
            >
              Learn about our approach <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {[
              { k: "1:6", v: "Instructor-to-student ratio target" },
              { k: "2yr", v: "Provider card validity" },
              { k: "5", v: "Programs offered equally" },
              { k: "DFW", v: "Service area coverage" },
            ].map((s) => (
              <div key={s.k} className="p-8 border border-gray-800 rounded-lg">
                <div className="font-display text-5xl font-black text-[#d90429]">{s.k}</div>
                <div className="mt-2 text-sm text-gray-400 uppercase tracking-wider">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GROUP CTA */}
      <section className="bg-[#f9fafb] border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-[#d90429] mb-4">Group & Workplace</div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-[#0a0a0a]">
              Train your entire team on your schedule.
            </h2>
            <p className="mt-6 text-gray-700 text-lg leading-relaxed max-w-2xl">
              We come to your workplace anywhere in the DFW area. Custom
              scheduling, group pricing, and programs tailored to your industry
              — from clinics to construction sites.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/group-training"
                data-testid="home-group-training-link"
                className="inline-flex items-center gap-2 rounded-md bg-[#0a0a0a] px-6 py-3 text-sm font-bold text-white hover:bg-[#d90429] transition-colors"
              >
                See Group Training Options
              </Link>
              <Link
                to="/contact?inquiry=group"
                data-testid="home-request-quote"
                className="inline-flex items-center gap-2 rounded-md border-2 border-[#0a0a0a] px-6 py-3 text-sm font-bold text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                Request a Quote
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={IMAGES.workplace_training}
              alt="Workplace safety training"
              className="w-full h-72 object-cover rounded-lg border border-gray-200"
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIAL PREVIEW */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center">
          <div className="font-mono-label text-gray-500 mb-6">Testimonials</div>
          <blockquote className="font-display text-3xl md:text-4xl font-bold text-[#0a0a0a] leading-tight">
            &ldquo;Hands-on from the first minute. Our whole team left feeling
            actually prepared — not just recertified.&rdquo;
          </blockquote>
          <div className="mt-8 text-gray-600">— DFW Clinic Manager (verified attendee)</div>
          <Link
            to="/testimonials"
            data-testid="home-testimonials-link"
            className="mt-8 inline-flex items-center gap-2 font-bold text-[#d90429] hover:text-black"
          >
            Read more testimonials <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#d90429]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-wrap gap-8 items-center justify-between">
          <div>
            <h2 className="font-display text-3xl md:text-5xl font-black text-white leading-tight">
              Ready to book your next class?
            </h2>
            <p className="mt-3 text-white/90 text-lg">
              Pick an available date and secure your spot in minutes.
            </p>
          </div>
          <Link
            to="/schedule"
            data-testid="footer-cta-signup"
            className="inline-flex items-center gap-2 rounded-md bg-white px-8 py-4 text-base font-bold text-[#d90429] hover:bg-black hover:text-white transition-colors"
          >
            View the Schedule <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
